import React from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';
import type { PatinfoSearchTermDto } from '../../../types/api/patinfo';
import type { ClientSearchValues } from '../../../services/clients/ClientCacheContracts';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';

export type ManageClientsSearchValues = ClientSearchValues;

const BLOCKSIZE_OPTIONS = ['10', '20', '30'] as const;

interface ManageClientsSearchBarProps {
  values: ManageClientsSearchValues;
  onValuesChange: (values: ManageClientsSearchValues) => void;
  onSearch: (terms: PatinfoSearchTermDto[], values: ManageClientsSearchValues) => void;
  isLoading: boolean;
  showPatId?: boolean;
  showDob?: boolean;
}

type MobileDateTimePickerType = React.ComponentType<{
  value: Date;
  mode: 'date';
  display: 'default';
  onChange: (event: any, selectedDate?: Date) => void;
}>;

const EMPTY: ManageClientsSearchValues = {
  fname: '',
  lname: '',
  patid: '',
  dob: '',
  blocksize: '10',
};

export const ManageClientsSearchBar: React.FC<ManageClientsSearchBarProps> = ({
  values,
  onValuesChange,
  onSearch,
  isLoading,
  showPatId = true,
  showDob = true,
}) => {
  const isWeb = Platform.OS === 'web';
  const [displayMenuOpen, setDisplayMenuOpen] = React.useState(false);
  const [selectedBlocksize, setSelectedBlocksize] = React.useState(values.blocksize || EMPTY.blocksize);
  const [showMobileDobPicker, setShowMobileDobPicker] = React.useState(false);
  const [MobileDateTimePicker, setMobileDateTimePicker] = React.useState<MobileDateTimePickerType | null>(null);

  // Determine if DOB should be in first column (under fname) or second column (under lname)
  const dobInFirstColumn = !showPatId && showDob;

  const webSelectStyle = { ...webControlStyle, width: 50, borderRadius: 5, padding: '0 6px' } as any;

  React.useEffect(() => {
    const next = values.blocksize || EMPTY.blocksize;
    setSelectedBlocksize((current) => (current === next ? current : next));
  }, [values.blocksize]);

  React.useEffect(() => {
    if (isWeb) return;

    try {
      const datePickerModule = require('@react-native-community/datetimepicker');
      const pickerComponent = (datePickerModule?.default ?? datePickerModule) as MobileDateTimePickerType | undefined;
      if (pickerComponent) {
        setMobileDateTimePicker(() => pickerComponent);
      }
    } catch (error) {
      console.warn('ManageClientsSearchBar: mobile datetime picker unavailable; using text fallback', error);
    }
  }, [isWeb]);

  const setField = (field: keyof ManageClientsSearchValues, value: string) => {
    onValuesChange({ ...values, [field]: value });
  };

  const handleSearch = () => {
    const terms: PatinfoSearchTermDto[] = [];
    if (values.fname.trim()) terms.push({ searchField: 'fname', searchTerm: values.fname.trim() });
    if (values.lname.trim()) terms.push({ searchField: 'lname', searchTerm: values.lname.trim() });
    if (values.patid.trim()) terms.push({ searchField: 'patid', searchTerm: values.patid.trim() });
    if (values.dob.trim()) terms.push({ searchField: 'dob', searchTerm: values.dob.trim() });
    onSearch(terms, { ...values, blocksize: selectedBlocksize });
  };

  const handleClear = () => {
    const cleared: ManageClientsSearchValues = {
      ...EMPTY,
      blocksize: selectedBlocksize,
    };
    onValuesChange(cleared);
  };

  const hasValues = [values.fname, values.lname, values.patid, values.dob].some(v => v.trim().length > 0);

  const displayValue = BLOCKSIZE_OPTIONS.includes(selectedBlocksize as any)
    ? selectedBlocksize
    : EMPTY.blocksize;

  const selectBlocksize = (blocksize: string) => {
    setSelectedBlocksize(blocksize);
    onValuesChange({ ...values, blocksize });
    setDisplayMenuOpen(false);
  };

  const handleMobileDobChange = (_event: any, selectedDate?: Date) => {
    setShowMobileDobPicker(false);
    if (selectedDate) {
      setField('dob', selectedDate.toISOString().slice(0, 10));
    }
  };

  const webDateInputStyle = { ...webControlStyle, width: 120, colorScheme: 'light' } as any;

  const webDateInputStyleEmpty = {
    ...webDateInputStyle,
    color: EDITOR_COLORS.placeholder,
  } as any;

  return (
    <View style={styles.container}>
      <View style={styles.tableCluster}>
        <View style={styles.columnSearch}>
          <TouchableOpacity
            onPress={handleSearch}
            style={[styles.searchBtn, isLoading && styles.searchBtnDisabled]}
            disabled={isLoading}
            accessibilityLabel="Search clients"
          >
            {isLoading ? (
              <ActivityIndicator size="small" color={EDITOR_COLORS.submitText} />
            ) : (
              <Text style={styles.searchBtnText}>🔍 Client Search</Text>
            )}
          </TouchableOpacity>
          {showPatId && <View style={styles.emptyCell} />}
        </View>

        <View style={styles.columnLabel}>
          <Text style={styles.label}>First</Text>
          {showPatId && <Text style={styles.label}>ID</Text>}
          {dobInFirstColumn && <Text style={styles.label}>DOB</Text>}
        </View>

        <View style={styles.columnInput}>
          <TextInput
            style={styles.input}
            value={values.fname}
            onChangeText={v => setField('fname', v)}
            autoCapitalize="words"
            returnKeyType="search"
            onSubmitEditing={handleSearch}
          />
          {showPatId && (
            <TextInput
              style={styles.input}
              value={values.patid}
              onChangeText={v => setField('patid', v)}
              autoCapitalize="none"
              returnKeyType="search"
              onSubmitEditing={handleSearch}
            />
          )}
          {dobInFirstColumn && (
            isWeb ? (
              React.createElement('input' as any, {
                type: 'date',
                value: values.dob,
                onChange: (event: any) => setField('dob', String(event?.target?.value ?? '')),
                style: values.dob ? webDateInputStyle : webDateInputStyleEmpty,
                'aria-label': 'Date of birth',
              })
            ) : (
              MobileDateTimePicker ? (
                <>
                  <TouchableOpacity
                    style={styles.mobileDateInputBtn}
                    onPress={() => setShowMobileDobPicker(true)}
                    accessibilityRole="button"
                    accessibilityLabel="Select date of birth"
                  >
                    <Text style={[styles.mobileDateInputText, !values.dob && styles.mobileDateInputPlaceholder]}>
                      {values.dob || 'YYYY-MM-DD'}
                    </Text>
                  </TouchableOpacity>
                  {showMobileDobPicker ? (
                    <MobileDateTimePicker
                      value={values.dob ? new Date(`${values.dob}T00:00:00`) : new Date()}
                      mode="date"
                      display="default"
                      onChange={handleMobileDobChange}
                    />
                  ) : null}
                </>
              ) : (
                <TextInput
                  style={styles.input}
                  value={values.dob}
                  onChangeText={v => setField('dob', v)}
                  returnKeyType="search"
                  maxLength={10}
                  onSubmitEditing={handleSearch}
                  placeholder="YYYY-MM-DD"
                />
              )
            )
          )}
        </View>

        <View style={styles.columnSpacer} />

        <View style={styles.columnLabel}>
          <Text style={styles.label}>Last</Text>
          {!dobInFirstColumn && showDob && <Text style={styles.label}>DOB</Text>}
        </View>

        <View style={styles.columnInput}>
          <TextInput
            style={styles.input}
            value={values.lname}
            onChangeText={v => setField('lname', v)}
            autoCapitalize="words"
            returnKeyType="search"
            onSubmitEditing={handleSearch}
          />
          {!dobInFirstColumn && showDob && (
            isWeb ? (
              React.createElement('input' as any, {
                type: 'date',
                value: values.dob,
                onChange: (event: any) => setField('dob', String(event?.target?.value ?? '')),
                style: values.dob ? webDateInputStyle : webDateInputStyleEmpty,
                'aria-label': 'Date of birth',
              })
            ) : (
              MobileDateTimePicker ? (
                <>
                  <TouchableOpacity
                    style={styles.mobileDateInputBtn}
                    onPress={() => setShowMobileDobPicker(true)}
                    accessibilityRole="button"
                    accessibilityLabel="Select date of birth"
                  >
                    <Text style={[styles.mobileDateInputText, !values.dob && styles.mobileDateInputPlaceholder]}>
                      {values.dob || 'YYYY-MM-DD'}
                    </Text>
                  </TouchableOpacity>
                  {showMobileDobPicker ? (
                    <MobileDateTimePicker
                      value={values.dob ? new Date(`${values.dob}T00:00:00`) : new Date()}
                      mode="date"
                      display="default"
                      onChange={handleMobileDobChange}
                    />
                  ) : null}
                </>
              ) : (
                <TextInput
                  style={styles.input}
                  value={values.dob}
                  onChangeText={v => setField('dob', v)}
                  returnKeyType="search"
                  maxLength={10}
                  onSubmitEditing={handleSearch}
                  placeholder="YYYY-MM-DD"
                />
              )
            )
          )}
        </View>

        <View style={styles.columnClear}>
          <TouchableOpacity
            onPress={handleClear}
            style={[styles.clearBtn, !hasValues && styles.clearBtnDisabled]}
            disabled={isLoading || !hasValues}
            accessibilityLabel="Clear search filters"
          >
            <Text style={styles.clearBtnText}>Clear</Text>
          </TouchableOpacity>
          {!dobInFirstColumn && showDob && <View style={styles.emptyCell} />}
        </View>

        <View style={styles.columnDisplay}>
          <View style={styles.displayRow}>
            <Text style={styles.displayLabel}>Display</Text>
            {isWeb ? (
              React.createElement(
                'select' as any,
                {
                  value: displayValue,
                  onChange: (event: any) => selectBlocksize(String(event?.target?.value ?? EMPTY.blocksize)),
                  style: webSelectStyle,
                  'aria-label': 'Set display block size',
                },
                BLOCKSIZE_OPTIONS.map((option) => (
                  React.createElement('option' as any, { key: option, value: option }, option)
                ))
              )
            ) : (
              <View style={styles.dropdownWrap}>
                <TouchableOpacity
                  onPress={() => setDisplayMenuOpen((open) => !open)}
                  style={styles.dropdownBtn}
                  accessibilityRole="button"
                  accessibilityLabel="Set display block size"
                >
                  <Text style={styles.dropdownBtnText}>{displayValue}</Text>
                  <Text style={styles.dropdownChevron}>{displayMenuOpen ? '▲' : '▼'}</Text>
                </TouchableOpacity>

                {displayMenuOpen ? (
                  <View style={styles.dropdownMenu}>
                    {BLOCKSIZE_OPTIONS.map((option) => (
                      <Pressable
                        key={option}
                        style={[styles.dropdownOption, displayValue === option && styles.dropdownOptionActive]}
                        onPressIn={() => selectBlocksize(option)}
                        onPress={() => selectBlocksize(option)}
                        accessibilityRole="button"
                        accessibilityLabel={`Set display ${option}`}
                      >
                        <Text
                          style={[
                            styles.dropdownOptionText,
                            displayValue === option && styles.dropdownOptionTextActive,
                          ]}
                        >
                          {option}
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                ) : null}
              </View>
            )}
          </View>
          {!dobInFirstColumn && showDob && <View style={styles.emptyCell} />}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    backgroundColor: EDITOR_COLORS.background,
    borderRadius: 14,
    padding: 16,
    marginBottom: 20,
    alignItems: 'center',
  },
  tableCluster: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 3,
  },
  columnSearch: {
    width: 138,
    gap: 4,
  },
  columnLabel: {
    width: 31,
    justifyContent: 'flex-start',
    gap: 4,
  },
  columnInput: {
    width: 120,
    justifyContent: 'flex-start',
    gap: 4,
  },
  columnSpacer: {
    width: 8,
  },
  columnClear: {
    width: 60,
    gap: 4,
    marginLeft: 3,
  },
  columnDisplay: {
    width: 140,
    gap: 4,
  },
  displayRow: {
    height: 26,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 6,
    width: '100%',
  },
  displayLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    textAlign: 'right',
  },
  emptyCell: {
    height: 26,
  },
  label: {
    ...editorText,
    height: 26,
    lineHeight: 26,
    color: EDITOR_COLORS.textMuted,
  },
  input: {
    ...editorText,
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
    width: '100%',
  },
  dropdownWrap: {
    position: 'relative',
  },
  dropdownBtn: {
    width: 50,
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 5,
    paddingHorizontal: 7,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: EDITOR_COLORS.background,
    gap: 6,
  },
  dropdownBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  dropdownChevron: {
    fontFamily: 'Arial',
    fontSize: 14,
    color: EDITOR_COLORS.textMuted,
  },
  dropdownMenu: {
    position: 'absolute',
    top: 28,
    right: 0,
    minWidth: 60,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    zIndex: 1000,
    elevation: 10,
    pointerEvents: 'auto',
    overflow: 'hidden',
  },
  dropdownOption: {
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  dropdownOptionActive: {
    backgroundColor: '#eff6ff',
  },
  dropdownOptionText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  dropdownOptionTextActive: {
    color: EDITOR_COLORS.accent,
  },
  clearBtn: {
    width: 50,
    height: 26,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    shadowColor: EDITOR_COLORS.placeholder,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 2,
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearBtnDisabled: {
    opacity: 0.5,
  },
  clearBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  searchBtn: {
    width: 120,
    height: 28,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.submitBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchBtnDisabled: {
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
  },
  searchBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  mobileDateInputBtn: {
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    justifyContent: 'center',
    backgroundColor: EDITOR_COLORS.background,
    width: '100%',
  },
  mobileDateInputText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  mobileDateInputPlaceholder: {
    color: EDITOR_COLORS.placeholder,
  },
});
